/**
 * Locale primitives — the one i18n module that is safe to import from a Server
 * Component.
 *
 * `lib/i18n.ts` owns the message catalogs and the React surface, which makes it
 * client-graph only (it imports `useSyncExternalStore`/`createContext`, neither
 * of which exists in React's `react-server` build). The root layout still needs
 * the cookie name and the locale parser to stamp `<html lang>` server-side
 * (backlog C4), so those constants live here: no React, no catalogs, no browser
 * APIs — importable from anywhere.
 */

export type Locale = "de" | "en";

/** The locales the catalogs ship — iteration source for cross-locale indexing. */
export const LOCALES: readonly Locale[] = ["de", "en"] as const;

/**
 * German is the product default AND the authoring locale: `de.json` is the
 * structural authority and the only catalog in the initial client bundle (C1).
 */
export const DEFAULT_LOCALE: Locale = "de";

/**
 * Cookie mirroring the active locale, read by the root layout so SSR renders
 * `<html lang>` correctly on the very first byte. Deliberately the same shape as
 * the `ui-theme` cookie (`lib/theme/theme-store.ts`): non-sensitive render
 * preference, readable server-side, no inline script, CSP-safe.
 */
export const LOCALE_COOKIE = "ui-locale";

/** localStorage key kept for the pre-cookie fast path / device-local memory. */
export const LOCALE_STORAGE_KEY = "preferred-locale";

/**
 * BCP-47 tag per UI locale — the SINGLE place a locale tag is spelled out.
 * `Intl` needs a region-qualified tag to pick date order and separators; the UI
 * locale codes ("de"/"en") deliberately stay region-free.
 */
export const BCP47: Record<Locale, string> = { de: "de-DE", en: "en-US" };

/** Narrows an untrusted value (cookie, localStorage, API) to a Locale. */
export function parseLocale(value: string | null | undefined): Locale | null {
  return value === "de" || value === "en" ? value : null;
}
